import React, { useState } from "react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/stories/accordian/accordian";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import { getProductSpecificFaqData } from "@/app/api/service/unlistedstocks";
import { useQuery } from "@tanstack/react-query";
import useMobile from "@/app/hooks/use-mobile";
import parse from "html-react-parser";

interface ItemType {
    ques: string;
    ans: string;
}

interface FaqProps {
    bgColor: string;
    compact: boolean;
    productType?: string;
    product?: string;
}

const StocksFaqs: React.FC<FaqProps> = ({ bgColor, productType, product }) => {
    const { data: productFaqData, isFetched: isProductFaqFetched } = useQuery({
        queryKey: ["productFaqListData"],
        queryFn: () => getProductSpecificFaqData(productType, product),
    });

    const { isMobile } = useMobile();
    const [showAllFaqs, setShowAllFaqs] = useState(false);

    const handleToggleFaqs = () => {
        setShowAllFaqs((prev) => !prev);
    };

    const productQuestions = productFaqData?.data?.questions[0]?.content || [];
    const displayedFaqs = showAllFaqs ? productQuestions : productQuestions.slice(0, 4);

    return (
        <div className={`${bgColor} mt-12 w-full max-w-[812px]`}>
            <h2 className="text-xl font-semibold sm:text-3xl">FAQs</h2>
            <div className="pb-s9 md:pb-s14">
                <Accordion type="single" collapsible={true} className="w-full max-w-full">
                    {displayedFaqs.map((item: ItemType, index: number) => (
                        <AccordionItem
                            key={index}
                            value={`item-${index}`}
                            className="data-[state=closed]:border-double data-[state=closed]:border-neutralOpacityDark-10"
                        >
                            <AccordionTrigger
                                showIcon={true}
                                className="tracking-loose py-s7 text-left text-base font-semibold leading-[32px] md:py-s9 md:text-lg"
                            >
                                {item?.ques}
                            </AccordionTrigger>
                            <AccordionContent className="tracking-loose pb-s9 pr-s15 pt-s3 text-left text-sm font-medium leading-[160%] tracking-[0.14px] text-neutral-500 data-[state=closed]:border-double data-[state=closed]:border-neutralOpacityDark-10">
                                {parse(item?.ans)}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
            {isProductFaqFetched && productQuestions.length >= 4 ? (
                <div className="flex items-center justify-center">
                    <RegularButton
                        noIcon
                        showRightIcon={false}
                        size={isMobile ? ButtonSize.Medium : ButtonSize.Large}
                        variant={ButtonVariant.SecondaryDefault}
                        label={showAllFaqs ? "Show Less" : "Show More"}
                        onClick={handleToggleFaqs}
                        className="w-full max-w-full md:w-auto"
                    />
                </div>
            ) : null}
        </div>
    );
};

export default StocksFaqs;
